import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import {
  DxChartModule,
  DxFunnelModule,
  DxPieChartModule,
  DxSelectBoxModule,
  DxTemplateModule,
} from "devextreme-angular";
import { DxiLegendModule } from "devextreme-angular/ui/nested";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { DevextremesharedmoduleModule } from "../../../../shared/Module/devextremeshared/devextremeshared.module";
import { SharedModule } from "primeng/api";
import { HierarchyFilterComponent } from "../../../../shared/components/hierarchy-filter/hierarchy-filter.component";

import { LanguageService } from "../../../../services/language.service";
import { UIUtils } from "../../../../services/uiutils.service";
import { AppStateManageService } from "../../../../services/app-state-manage.service";
import { FinancialYearUtilsService } from "../../../../services/current-financial-year.service";
import { DashboardFilterService } from "../../../../services/dashboard_filter_dependent_multiselection.service";
import { DashboardFilterStateStorageService } from "../../../../services/dashboard-filter-state-storage.service";

import { FinancialYear } from "../../../../classes/domain/entities/website/financialyear/financialyearmaster";
import {
  DomainEnums,
  DrillDownRequestTypes,
  DrillDownSubTypes,
  FromEntityTypes,
  ReportFilterTypes,
} from "../../../../classes/infrastructure/enums";
import { TransportData } from "../../../../classes/infrastructure/transportdata";
import { DataContainerService } from "../../../../classes/infrastructure/datacontainer.service";
import { Dashboards } from "../../../../classes/domain/entities/website/dashboard/dashboard";
import { PayloadPacketFacade } from "../../../../classes/infrastructure/payloadpacket/payloadpacketfacade";
import { ServerCommunicatorService } from "../../../../services/server-communicator.service";
import { DashboardsFetchRequest } from "../../../../classes/domain/entities/website/dashboard/dashboardfetchrequest";
import { DRILLDOWN_ROUTE_PATH } from "../../../../classes/domain/entities/website/dashboard/dashboarddrilldown/dashboarddrilldowntarget";
import { DRILLDOWN_TYPES_REQUIRING_SUBTYPE } from "../../../../classes/domain/entities/website/dashboard/dashboarddrilldown/dashboarddrilldowncontext.service";
import { CurrentDateTimeRequest } from "@app/classes/infrastructure/request_response/currentdatetimerequest";
import { TodaysTaskFetchRequest } from "@app/classes/domain/entities/website/dashboard/todaystasklist/todaystasklistfetchrequest";
import { TodaysTask } from "@app/classes/domain/entities/website/dashboard/todaystasklist/todaystasklist";
import { SUBMENU_ID } from "@app/classes/domain/constants";

interface TodayTask {
  id: string;
  title: string;
  titleMr: string;
  count: string;
  iconImg?: string;
  emoji?: string;
  from_entity_type?: number;
}

const TASK_ICON_BY_ENTITY: Record<number, string> = {
  [FromEntityTypes.anc_register_master]: "assets/dashboard_icons/today_task_icon/anc_visit.png",
  [FromEntityTypes.anc_follow_up_master]: "assets/dashboard_icons/today_task_icon/anc_visit.png",
  [FromEntityTypes.maata_register_master]: "assets/dashboard_icons/today_task_icon/anc_visit.png",
  [FromEntityTypes.child_immunization_register_master]: "assets/dashboard_icons/today_task_icon/child_immunization_visit.png",
  [FromEntityTypes.hbnc_follow_up_master]: "assets/dashboard_icons/today_task_icon/hbnc_visit.png",
  [FromEntityTypes.pnc_follow_up_master]: "assets/dashboard_icons/today_task_icon/pnc_visit.png",
  [FromEntityTypes.abortion_follow_up_master]: "assets/dashboard_icons/today_task_icon/abortion_visit.png",
  [FromEntityTypes.miscarriage_follow_up_master]: "assets/dashboard_icons/today_task_icon/miscarriage_visit.png",
};

function getTodayTaskIcon(entityType?: number, idOrTitle?: string): string | undefined {
  if (entityType && TASK_ICON_BY_ENTITY[entityType]) {
    return TASK_ICON_BY_ENTITY[entityType];
  }
  const str = (idOrTitle || "").toLowerCase();
  if (str.includes("anc") || str.includes("pregnant") || str.includes("maata") || str.includes("mata") || str.includes("माता")) {
    return "assets/dashboard_icons/today_task_icon/anc_visit.png";
  }
  if (str.includes("immun") || str.includes("child") || str.includes("लसीकरण") || str.includes("बाल")) {
    return "assets/dashboard_icons/today_task_icon/child_immunization_visit.png";
  }
  if (str.includes("hbnc")) {
    return "assets/dashboard_icons/today_task_icon/hbnc_visit.png";
  }
  if (str.includes("pnc")) {
    return "assets/dashboard_icons/today_task_icon/pnc_visit.png";
  }
  if (str.includes("abort") || str.includes("गर्भपात")) {
    return "assets/dashboard_icons/today_task_icon/abortion_visit.png";
  }
  if (str.includes("miscarriage") || str.includes("गर्भस्राव")) {
    return "assets/dashboard_icons/today_task_icon/miscarriage_visit.png";
  }
  return undefined;
}

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    TranslateModule,
    DevextremesharedmoduleModule,
    DxPieChartModule,
    DxiLegendModule,
    DxChartModule,
    DxFunnelModule,
    DxSelectBoxModule,
    DxTemplateModule,
    HierarchyFilterComponent,
  ],
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.scss",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DashboardComponent implements OnInit, OnDestroy {
  // -------------------------------------------------------------
  // USER INFO & PROFILE
  // -------------------------------------------------------------
  userDisplayName: string = "Mrs. Vidya Vishwas Patil";
  roleName: string = "ASHA";
  locationText: string = "PHC: Sagroli | SC: Sagroli | Village: Sagroli";
  profileImageUrl: string | null = null;

  // -------------------------------------------------------------
  // DATES & GREETING
  // -------------------------------------------------------------
  todayDateFormatted: string = "";
  todayDayOfWeek: string = "";

  constructor(
    private uiUtils: UIUtils,
    private appStateManage: AppStateManageService,
    private customDate: FinancialYearUtilsService,
    public dashboardFilterService: DashboardFilterService,
    public filterStorage: DashboardFilterStateStorageService,
    public langService: LanguageService,
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.todayDateFormatted = this.getFormattedDate(new Date());
    this.todayDayOfWeek = this.getDayName(new Date());
    this.updateChartDataSources();
  }

  serverAsOnDateFormatted: string = "";

  get asOnDate(): string {
    const isMr = Boolean(this.langService && this.langService.currentLang === "mr");
    if (this.serverAsOnDateFormatted && this.serverAsOnDateFormatted.trim()) {
      const val = this.serverAsOnDateFormatted.trim();
      if (val.toLowerCase().startsWith("as on") || val.startsWith("दि.") || val.includes("रोजी")) {
        return val;
      }
      return isMr ? `दि. ${val} रोजी` : `as on ${val}`;
    }
    const formatted = this.todayDateFormatted || this.getFormattedDate(new Date());
    return isMr ? `दि. ${formatted} रोजी` : `as on ${formatted}`;
  }
  get keyFiguresDate(): string { return this.asOnDate; }
  get familyDate(): string { return this.asOnDate; }
  get ecrDate(): string { return this.asOnDate; }
  get ancDate(): string { return this.asOnDate; }
  get birthDate(): string { return this.asOnDate; }
  get hbncDate(): string { return this.asOnDate; }
  get immunizationDate(): string { return this.asOnDate; }
  get deathDate(): string { return this.asOnDate; }
  get pncDate(): string { return this.asOnDate; }
  get pregnancyLossDate(): string { return this.asOnDate; }

  getFormattedDate(d: Date): string {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${d.getFullYear()}`;
  }

  getDayName(d: Date): string {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const daysMr = [
      "रविवार",
      "सोमवार",
      "मंगळवार",
      "बुधवार",
      "गुरुवार",
      "शुक्रवार",
      "शनिवार",
    ];
    try {
      const isMr = Boolean(this.langService && this.langService.currentLang === "mr");
      return isMr ? daysMr[d.getDay()] : days[d.getDay()];
    } catch {
      return days[d.getDay()];
    }
  }

  // -------------------------------------------------------------
  // FILTER BAR STATE
  // -------------------------------------------------------------
  YearsList: FinancialYear[] = [];
  CurrentFinancialYear: string = "2026-2027";
  FinancialYearRef: number = 0;
  financialYearStart!: Date;
  financialYearEnd!: Date;

  FilterList = DomainEnums.ReportFilterTypesListLang(false, "REPORT_PLACEHOLDERS.Select");
  MonthList = DomainEnums.MonthListLang(true, "REPORT_PLACEHOLDERS.SelectMonth");
  QuarterList = DomainEnums.QuarterTypesListLang(true, "REPORT_PLACEHOLDERS.SelectQuarter");
  HalfYearList = DomainEnums.HalfYearListLang(true, "REPORT_PLACEHOLDERS.SelectHalfYear");

  ReportFilterType: number = ReportFilterTypes.Yearly;
  Month: number = 0;
  QuarterlyYearType: number = 0;
  HalfYearType: number = 0;

  IsShowMonth: boolean = false;
  IsShowQuarter: boolean = false;
  IsShowHalfYear: boolean = false;
  IsShowDates: boolean = false;
  IsPieTempHide: boolean = true;

  // Rural / Urban toggle
  selectedArea: "rural" | "urban" = "rural";
  isUrbanNoData: boolean = false;

  // Refresh & Stay Detection State
  isRefreshing: boolean = false;
  isDataStale: boolean = false;
  isBannerDismissed: boolean = false;
  isGuideDismissed: boolean = false;
  justUpdated: boolean = false;
  lastUpdatedTime: Date | null = null;
  readonly STALE_TIMEOUT_MS: number = 2 * 60 * 1000; // 2 minutes stay threshold
  private staleStayTimer: any = null;
  private visibilityHandler: any = null;
  private updatedNoticeTimer: any = null;

  // -------------------------------------------------------------
  // TODAY'S TASK
  // -------------------------------------------------------------
  isTodayTasksLive: boolean = false;
  allTodayTasks: TodayTask[] = [];   // start empty – no STATIC_TODAY_TASKS
  private readonly DASHBOARD_TASK_LIMIT = 3; // or 3 – whatever you want

  get todayTasks(): TodayTask[] {
    // always show up to limit (1 task if limit is 1 and data exists)
    return this.allTodayTasks.slice(0, this.DASHBOARD_TASK_LIMIT);
  }

  get hasMoreTasks(): boolean {
    // View All only when total > limit
    return this.allTodayTasks.length > this.DASHBOARD_TASK_LIMIT;
  }

  get totalTodayTasksCount(): string {
    const total = this.allTodayTasks.reduce(
      (sum, t) => sum + (parseInt(t.count, 10) || 0),
      0
    );
    return String(total).padStart(2, "0");
  }
  // -------------------------------------------------------------
  // KEY FIGURES
  // -------------------------------------------------------------
  totalPopulationCoveredByAsha: number = 0;
  totalPopulationPercentage: number = 0;
  totalPopulationAssignedToAsha: number = 0;
  noOfFamilies: number = 0;
  noOfFamiliesPercentage: number = 0;
  childrenBelowSixYears: number = 0;
  childrenBelowFiveYearsPercentage: number = 0;
  membersAboveThirtyYears: number = 0;
  membersAboveThirtyYearsPercentage: number = 0;
  membersAboveSixtyFiveYears: number = 0;
  membersAboveSixtyFiveYearsPercentage: number = 0;

  // -------------------------------------------------------------
  // OVERVIEW: 1. FAMILY REGISTRATION (Stacked Bar: Pending & Approved -> Migrated & Native)
  // -------------------------------------------------------------
  familyTotalFamilies: number = 0;

  // Pending breakdown: Native & Migrated
  familyPendingTotal: number = 0;
  familyPendingNative: number = 0;
  familyPendingMigrated: number = 0;

  // Approved breakdown: Native & Migrated
  familyApprovedTotal: number = 0;
  familyApprovedNative: number = 0;
  familyApprovedMigrated: number = 0;

  get familyTotalNative(): number {
    return this.familyApprovedNative + this.familyPendingNative;
  }
  get familyTotalMigrated(): number {
    return this.familyApprovedMigrated + this.familyPendingMigrated;
  }

  // DevExtreme Stacked Bar Chart DataSource for Family Registration (kept for backward compatibility)
  familyStackedData: any[] = [];

  // Family Registration: Dual Donut Charts
  familyStatusPalette: string[] = ["#f472b6", "#93c5fd"];
  familyOriginPalette: string[] = ["#10b981", "#f59e0b"];

  familyStatusData: any[] = [];
  familyOriginData: any[] = [];
  hoveredDonut: Record<string, boolean> = {};

  onDonutPointHoverChanged(donutKey: string, e: any): void {
    this.hoveredDonut[donutKey] = !!(e.target && e.target.isHovered());
  }

  onDonutMouseLeave(donutKey: string): void {
    this.hoveredDonut[donutKey] = false;
  }

  onFamilyStatusChartPointClick(e: any): void {
    const code = e.target?.data?.statusCode;
    if (code) {
      this.onFamilyClick(code);
    } else {
      this.onFamilyClick("Approved");
    }
  }

  onFamilyOriginChartPointClick(e: any): void {
    const code = e.target?.data?.statusCode;
    if (code) {
      this.onFamilyClick(code);
    } else {
      this.onFamilyClick("ApprovedNative");
    }
  }

  customizeFamilyStackedTooltip = (arg: any) => {
    return {
      text: `${arg.seriesName}: <b>${arg.valueText}</b> (${arg.argumentText})`,
    };
  };

  onFamilyChartPointClick(e: any): void {
    const status: string | undefined = e.target?.data?.statusCode; // "Approved" | "Pending"
    const valueField: string | undefined = e.target?.series?.getValueFields?.()[0]; // "native" | "migrated"

    if (status && valueField === "native") {
      this.onFamilyClick(status === "Approved" ? "ApprovedNative" : "PendingNative");
    } else if (status && valueField === "migrated") {
      this.onFamilyClick(status === "Approved" ? "ApprovedMigrated" : "PendingMigrated");
    } else if (status === "Approved" || status === "Pending") {
      this.onFamilyClick(status);
    } else {
      this.onFamilyClick("Total");
    }
  }

  // 2-Column Stacked Bar Pixel Heights (Pending & Approved -> Migrated & Native)
  get familyMaxBarVal(): number {
    const app = Number(this.familyApprovedTotal) || 0;
    const pend = Number(this.familyPendingTotal) || 0;
    return Math.max(app, pend, 60);
  }

  get familyPendingNativeHeightPx(): number {
    const val = Number(this.familyPendingNative) || 0;
    const max = this.familyMaxBarVal || 60;
    const h = Math.round((val / max) * 110);
    return Math.max(22, isNaN(h) ? 22 : h);
  }
  get familyPendingMigratedHeightPx(): number {
    const val = Number(this.familyPendingMigrated) || 0;
    const max = this.familyMaxBarVal || 60;
    const h = Math.round((val / max) * 110);
    return Math.max(18, isNaN(h) ? 18 : h);
  }

  get familyApprovedNativeHeightPx(): number {
    const val = Number(this.familyApprovedNative) || 0;
    const max = this.familyMaxBarVal || 60;
    const h = Math.round((val / max) * 110);
    return Math.max(22, isNaN(h) ? 22 : h);
  }
  get familyApprovedMigratedHeightPx(): number {
    const val = Number(this.familyApprovedMigrated) || 0;
    const max = this.familyMaxBarVal || 60;
    const h = Math.round((val / max) * 110);
    return Math.max(18, isNaN(h) ? 18 : h);
  }

  // -------------------------------------------------------------
  // OVERVIEW: 2. ELIGIBLE COUPLE FOLDER
  // -------------------------------------------------------------
  ecrTotal: number = 0;
  get ecrEligibleCouples(): number { return this.ecrTotal; }
  ecrProtectionRate: number = 0;
  ecrSterilizedTotal: number = 0;
  ecrMaleSterilization: number = 0;
  ecrFemaleSterilization: number = 0;
  ecrInfertileCouples: number = 0;
  ecrFamilyPlanningUsage: number = 0;

  // -------------------------------------------------------------
  // OVERVIEW: 3. ANC (Antenatal Care)
  // -------------------------------------------------------------
  ancTotal: number = 0;
  ancFullyProtected: number = 0;
  ancNormalMaata: number = 0;
  ancExpectedDeliveries: number = 0;
  ancPregnancyRegistered12Weeks: number = 0;
  ancHighRisk: number = 0;
  ancSevereAnemia: number = 0;

  ancPalette: string[] = ["#22d3ee", "#34d399", "#f59e0b", "#a855f7", "#ec4899", "#f97316"];
  hbncPalette: string[] = ["#f97316", "#06b6d4", "#eab308", "#3b82f6", "#10b981", "#f43f5e", "#84cc16", "#a855f7"];
  deathPalette: string[] = ["#06b6d4", "#8b5cf6", "#3b82f6", "#ef4444", "#ec4899"];
  pncPalette: string[] = ["#06b6d4", "#eab308", "#a855f7", "#84cc16"];

  customizePiePoint = (arg: any) => {
    const color = arg?.tag || arg?.point?.data?.color || arg?.data?.color;
    if (color) return { color };
    return {};
  };

  customizeTooltip = (arg: any) => {
    const title = arg.argumentText || arg.argument || "";
    const val = arg.valueText || arg.value || 0;
    const color = (arg.point?.getColor ? arg.point.getColor() : null) || arg.point?.data?.color || arg.point?.tag || "#38bdf8";
    let percentStr = "";
    if (arg.percentText) {
      percentStr = `(${arg.percentText})`;
    } else if (arg.percent != null && !isNaN(arg.percent)) {
      percentStr = `(${((arg.percent * 100)).toFixed(1)}%)`;
    }
    return {
      html: `
        <div class="ud-dx-tooltip-inner" style="color: #ffffff; white-space: nowrap; display: flex; flex-direction: column; gap: 3px; min-width: 110px; font-family: 'Poppins', sans-serif;">
          <div class="ud-tooltip-header" style="display: flex; align-items: center; gap: 7px;">
            <span class="ud-tooltip-dot" style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${color}; box-shadow: 0 0 6px rgba(255, 255, 255, 0.35); flex-shrink: 0;"></span>
            <span class="ud-tooltip-title" style="font-size: 11.5px; font-weight: 500; color: #cbd5e1; letter-spacing: 0.2px;">${title}</span>
          </div>
          <div class="ud-tooltip-body" style="display: flex; align-items: baseline; gap: 6px; padding-left: 15px;">
            <span class="ud-tooltip-value" style="font-size: 14px; font-weight: 700; color: #ffffff; line-height: 1.1;">${val}</span>
            ${percentStr ? `<span class="ud-tooltip-percent" style="font-size: 11px; font-weight: 600; color: #38bdf8;">${percentStr}</span>` : ""}
          </div>
        </div>
      `,
    };
  };

  getEcrPercent(val: number): string {
    const v = Number(val) || 0;
    const total = Number(this.ecrTotal) || 0;
    if (total <= 0) return "0.0%";
    const pct = (v / total) * 100;
    return `${(isNaN(pct) ? 0 : pct).toFixed(1)}%`;
  }

  ancDonutData: any[] = [];

  onAncChartPointClick(e: any): void {
    const code = e.target?.data?.code;
    if (code) this.onAncClick(code);
  }

  // -------------------------------------------------------------
  // OVERVIEW: 4. BIRTH REGISTER
  // -------------------------------------------------------------
  birthTotal: number = 0;
  birthMale: number = 0;
  birthFemale: number = 0;

  // -------------------------------------------------------------
  // OVERVIEW: 5. HBNC REGISTER
  // -------------------------------------------------------------
  hbncTotal: number = 0;
  hbncRegistered: number = 0;
  hbncVisitsCompleted: number = 0;
  hbncZeroVisitsCompleted: number = 0;
  hbncDefectiveChildren: number = 0;
  hbncChildDeath: number = 0;
  hbncNICU: number = 0;
  hbncNBSU: number = 0;
  hbncSNCU: number = 0;

  hbncDonutData: any[] = [];

  onHbncChartPointClick(e: any): void {
    const code = e.target?.data?.code;
    if (code) this.onHbncClick(code);
  }

  // -------------------------------------------------------------
  // OVERVIEW: 6. CHILD IMMUNIZATION
  // -------------------------------------------------------------
  immunizationTotal: number = 0;
  immunizationFully: number = 0;
  immunizationNoVaccine: number = 0;
  immunizationStartedNotCompleted: number = 0;

  immunizationChartData: any[] = [];

  customizeImmunizationPoint = (arg: any) => {
    const color = arg?.tag || arg?.point?.data?.color || arg?.data?.color || "#10b981";
    return { color };
  };

  customizeImmunizationTooltip = (arg: any) => {
    const title = arg.argumentText || arg.argument || "";
    const val = Number(arg.value) || 0;
    const color = arg.point?.tag || (arg.point?.getColor ? arg.point.getColor() : null) || arg.point?.data?.color || "#10b981";
    const total = (this.immunizationFully || 0) + (this.immunizationNoVaccine || 0) + (this.immunizationStartedNotCompleted || 0);
    const percentStr = total > 0 ? `(${((val / total) * 100).toFixed(1)}%)` : "";
    return {
      html: `
        <div class="ud-dx-tooltip-inner" style="color: #ffffff; white-space: nowrap; display: flex; flex-direction: column; gap: 3px; min-width: 110px; font-family: 'Poppins', sans-serif;">
          <div class="ud-tooltip-header" style="display: flex; align-items: center; gap: 7px;">
            <span class="ud-tooltip-dot" style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${color}; box-shadow: 0 0 6px rgba(255, 255, 255, 0.35); flex-shrink: 0;"></span>
            <span class="ud-tooltip-title" style="font-size: 11.5px; font-weight: 500; color: #cbd5e1; letter-spacing: 0.2px;">${title}</span>
          </div>
          <div class="ud-tooltip-body" style="display: flex; align-items: baseline; gap: 6px; padding-left: 15px;">
            <span class="ud-tooltip-value" style="font-size: 14px; font-weight: 700; color: #ffffff; line-height: 1.1;">${arg.valueText || val}</span>
            ${percentStr ? `<span class="ud-tooltip-percent" style="font-size: 11px; font-weight: 600; color: #38bdf8;">${percentStr}</span>` : ""}
          </div>
        </div>
      `,
    };
  };

  onImmunizationChartPointClick(e: any): void {
    const code = e.target?.data?.code;
    if (code) {
      this.onImmunizationClick(code);
    } else {
      const name = e.target?.data?.name || "";
      if (name.includes("Fully") || name.includes("पूर्ण")) {
        this.onImmunizationClick("fully");
      } else if (name.includes("no") || name.includes("नाही")) {
        this.onImmunizationClick("noVaccine");
      } else {
        this.onImmunizationClick("startedNotCompleted");
      }
    }
  }

  get immFullyHeight(): string {
    const fully = Number(this.immunizationFully) || 0;
    const noVac = Number(this.immunizationNoVaccine) || 0;
    const started = Number(this.immunizationStartedNotCompleted) || 0;
    const max = Math.max(fully, noVac, started, 150);
    const h = Math.round((fully / max) * 85);
    return Math.max(14, isNaN(h) ? 14 : h) + "px";
  }
  get immZeroHeight(): string {
    const fully = Number(this.immunizationFully) || 0;
    const noVac = Number(this.immunizationNoVaccine) || 0;
    const started = Number(this.immunizationStartedNotCompleted) || 0;
    const max = Math.max(fully, noVac, started, 150);
    const h = Math.round((noVac / max) * 85);
    return Math.max(14, isNaN(h) ? 14 : h) + "px";
  }
  get immStartedHeight(): string {
    const fully = Number(this.immunizationFully) || 0;
    const noVac = Number(this.immunizationNoVaccine) || 0;
    const started = Number(this.immunizationStartedNotCompleted) || 0;
    const max = Math.max(fully, noVac, started, 150);
    const h = Math.round((started / max) * 85);
    return Math.max(14, isNaN(h) ? 14 : h) + "px";
  }

  // -------------------------------------------------------------
  // OVERVIEW: 7. DEATH REGISTER
  // -------------------------------------------------------------
  deathTotal: number = 0;
  deathANC: number = 0;
  deathStillBirth: number = 0;
  death0to5Years: number = 0;
  death30PlusYears: number = 0;
  death65PlusYears: number = 0;

  deathDonutData: any[] = [];

  onDeathChartPointClick(e: any): void {
    const code = e.target?.data?.code;
    if (code) this.onDeathClick(code);
  }

  // -------------------------------------------------------------
  // OVERVIEW: 8. PNC (Postnatal Care)
  // -------------------------------------------------------------
  pncTotal: number = 0;
  pncVisitsCompleted: number = 0;
  pncHighRisk: number = 0;
  pncZeroVisitCompleted: number = 0;
  pncNormal: number = 0;

  pncDonutData: any[] = [];

  onPncChartPointClick(e: any): void {
    const code = e.target?.data?.code;
    if (code) this.onPncClick(code);
  }

  updateChartDataSources(): void {
    const isMr = Boolean(this.langService && this.langService.currentLang === "mr");

    this.familyStackedData = [
      {
        category: isMr ? "प्रलंबित" : "Pending",
        statusCode: "Pending",
        native: Number(this.familyPendingNative) || 0,
        migrated: Number(this.familyPendingMigrated) || 0,
        total: Number(this.familyPendingTotal) || 0,
      },
      {
        category: isMr ? "मंजूर" : "Approved",
        statusCode: "Approved",
        native: Number(this.familyApprovedNative) || 0,
        migrated: Number(this.familyApprovedMigrated) || 0,
        total: Number(this.familyApprovedTotal) || 0,
      },
    ];

    this.familyStatusData = [
      {
        name: isMr ? "मंजूर" : "Approved",
        value: Number(this.familyApprovedTotal) || 0,
        color: "#f472b6",
        statusCode: "Approved",
      },
      {
        name: isMr ? "प्रलंबित" : "Pending",
        value: Number(this.familyPendingTotal) || 0,
        color: "#93c5fd",
        statusCode: "Pending",
      },
    ];

    this.familyOriginData = [
      {
        name: isMr ? "मूळ रहिवासी" : "Native",
        value: Number(this.familyApprovedNative) || 0,
        color: "#10b981",
        statusCode: "ApprovedNative",
      },
      {
        name: isMr ? "स्थलांतरित" : "Migrated",
        value: Number(this.familyApprovedMigrated) || 0,
        color: "#f59e0b",
        statusCode: "ApprovedMigrated",
      },
    ];

    this.ancDonutData = [
      { name: isMr ? "पूर्ण संरक्षित एएनसी" : "Fully Protected ANC", value: Number(this.ancFullyProtected) || 0, color: "#22d3ee", code: 'fullyProtected' },
      { name: isMr ? "सामान्य माता" : "Normal Maata", value: Number(this.ancNormalMaata) || 0, color: "#34d399", code: 'normal' },
      { name: isMr ? "प्रसूती अपेक्षित" : "Expected Deliveries", value: Number(this.ancExpectedDeliveries) || 0, color: "#f59e0b", code: 'expected' },
      { name: isMr ? "१२ आठवड्यांत नोंदणीकृत गर्भधारणा" : "Pregnancy Reg Within 12 Wks", value: Number(this.ancPregnancyRegistered12Weeks) || 0, color: "#a855f7", code: 'within12Weeks' },
      { name: isMr ? "उच्च जोखीम माता" : "High-Risk Maata", value: Number(this.ancHighRisk) || 0, color: "#ec4899", code: 'highRisk' },
      { name: isMr ? "तीव्र रक्तक्षय" : "Severe Anemia", value: Number(this.ancSevereAnemia) || 0, color: "#f97316", code: 'severeAnemia' },
    ];

    this.hbncDonutData = [
      { name: isMr ? "एकूण नोंदणीकृत HBNC (किमान १ भेट पूर्ण)" : "Total HBNC Registered (at least 1 visit completed)", value: Number(this.hbncRegistered) || 0, color: "#f97316", code: 'registered' },
      { name: isMr ? "पूर्ण झालेल्या भेटी" : "Visits Completed", value: Number(this.hbncVisitsCompleted) || 0, color: "#06b6d4", code: 'completed' },
      { name: isMr ? "एकही HBNC भेट पूर्ण नाही" : "No Visit Completed", value: Number(this.hbncZeroVisitsCompleted) || 0, color: "#eab308", code: 'zeroVisits' },
      { name: isMr ? "जन्मजात दोष असलेली मुले" : "Defective Children", value: Number(this.hbncDefectiveChildren) || 0, color: "#3b82f6", code: 'defective' },
      { name: isMr ? "बालमृत्यू" : "Child Death", value: Number(this.hbncChildDeath) || 0, color: "#10b981", code: 'death' },
      { name: isMr ? "नवजात अतिदक्षता कक्ष" : "NICU", value: Number(this.hbncNICU) || 0, color: "#f43f5e", code: 'nicu' },
      { name: isMr ? "नवजात स्थिरीकरण कक्ष" : "NBSU", value: Number(this.hbncNBSU) || 0, color: "#84cc16", code: 'kmc' },
      { name: isMr ? "विशेष नवजात शिशु उपचार कक्ष" : "SNCU", value: Number(this.hbncSNCU) || 0, color: "#a855f7", code: 'sncu' },
    ];

    this.immunizationChartData = [
      { name: isMr ? "पूर्ण लसीकरण" : "Fully Immunized", value: Number(this.immunizationFully) || 0, color: "#10b981", code: 'fully' },
      { name: isMr ? "लसीकरण सुरू नाही" : "No Vaccination", value: Number(this.immunizationNoVaccine) || 0, color: "#38bdf8", code: 'noVaccine' },
      { name: isMr ? "लसीकरण अपूर्ण" : "Incomplete", value: Number(this.immunizationStartedNotCompleted) || 0, color: "#f97316", code: 'startedNotCompleted' },
    ];

    this.deathDonutData = [
      { name: isMr ? "गर्भावस्थेतील मृत्यू" : "ANC Death", value: Number(this.deathANC) || 0, color: "#06b6d4", code: 'anc' },
      { name: isMr ? "मृत जन्म" : "Still Birth", value: Number(this.deathStillBirth) || 0, color: "#8b5cf6", code: 'still' },
      { name: isMr ? "०-५ वर्षे मृत्यू" : "0-5 Years Death", value: Number(this.death0to5Years) || 0, color: "#3b82f6", code: '0to5' },
      { name: isMr ? "३०+ वर्षे मृत्यू" : "30+ Year Death", value: Number(this.death30PlusYears) || 0, color: "#ef4444", code: '30plus' },
      { name: isMr ? "६५+ वर्षे मृत्यू" : "65+ Year Death", value: Number(this.death65PlusYears) || 0, color: "#ec4899", code: '65plus' },
    ];

    this.pncDonutData = [
      { name: isMr ? "एकूण नोंदणीकृत PNC (किमान १ भेट पूर्ण)" : "Total PNC Registered (at least 1 visit completed)", value: Number(this.pncVisitsCompleted) || 0, color: "#06b6d4", code: 'completed' },
      { name: isMr ? "एकही PNC भेट पूर्ण नाही" : "No Visit Completed", value: Number(this.pncZeroVisitCompleted) || 0, color: "#eab308", code: 'zeroVisits' },
      { name: isMr ? "उच्च जोखीम PNC" : "High-Risk PNC", value: Number(this.pncHighRisk) || 0, color: "#a855f7", code: 'highRisk' },
      { name: isMr ? "सामान्य PNC" : "Normal PNC", value: Number(this.pncNormal) || 0, color: "#84cc16", code: 'normal' },
    ];

    try {
      this.cdr.markForCheck();
    } catch { }
  }

  // -------------------------------------------------------------
  // OVERVIEW: 9. PREGNANCY LOSS
  // -------------------------------------------------------------
  pregnancyLossTotal: number = 0;
  pregnancyLossAbortion: number = 0;
  pregnancyLossMiscarriage: number = 0;

  // -------------------------------------------------------------
  // SVG DONUT CHARTS CALCULATION
  // -------------------------------------------------------------
  getSvgDonutSlices(data: { name: string; value: number; color?: string }[]): {
    name: string;
    value: number;
    color: string;
    percent: string;
    dashArray: string;
    dashOffset: number;
  }[] {
    const safeData = (data || []).map((d) => ({
      ...d,
      value: Number(d?.value) || 0,
    }));
    const total = safeData.reduce((acc, item) => acc + item.value, 0) || 1;
    let cumulative = 0;
    return safeData.map((item) => {
      const val = item.value;
      const rawPct = (val / total) * 100;
      const pct = Math.max(0, Math.min(100, isNaN(rawPct) ? 0 : rawPct));
      const slice = {
        name: item.name || "",
        value: val,
        color: item.color || "#06b6d4",
        percent: `${pct.toFixed(1)}%`,
        dashArray: `${pct.toFixed(2)} ${(100 - pct).toFixed(2)}`,
        dashOffset: isNaN(cumulative) ? 0 : -cumulative,
      };
      cumulative += pct;
      return slice;
    });
  }

  // -------------------------------------------------------------
  // CHART TOOLTIP (HOVER FOR ALL CHARTS)
  // -------------------------------------------------------------
  chartTooltip = {
    visible: false,
    x: 0,
    y: 0,
    title: "",
    value: 0,
    percent: "",
    color: "#3b82f6",
  };

  getSterilizationPercent(val: number): string {
    const v = Number(val) || 0;
    const total =
      (Number(this.ecrMaleSterilization) || 0) + (Number(this.ecrFemaleSterilization) || 0) ||
      (Number(this.ecrSterilizedTotal) || 0);
    if (!total || isNaN(total) || total <= 0) return "0%";
    const pct = (v / total) * 100;
    return `${(isNaN(pct) ? 0 : pct).toFixed(1)}%`;
  }

  getSterilizationFillPercent(val: number): number {
    const v = Number(val) || 0;
    const total =
      (Number(this.ecrMaleSterilization) || 0) + (Number(this.ecrFemaleSterilization) || 0) ||
      (Number(this.ecrSterilizedTotal) || 0);
    if (!total || isNaN(total) || total <= 0) return 0;
    const pct = Math.round((v / total) * 100);
    return Math.min(100, Math.max(0, isNaN(pct) ? 0 : pct));
  }

  formatTrend(pct: number | null | undefined): string {
    const val = Number(pct);
    if (isNaN(val) || pct === null || pct === undefined) {
      return "+ 0%";
    }
    const formatted = Math.abs(val) % 1 === 0 ? val.toString() : val.toFixed(1);
    if (val > 0) return `+ ${formatted}%`;
    return `${formatted}%`;
  }

  get chartTooltipStyle(): { [key: string]: string } {
    const isNearTop = this.chartTooltip.y < 85;
    const screenWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
    const clampedX = Math.max(90, Math.min(screenWidth - 90, this.chartTooltip.x));
    return {
      left: `${clampedX}px`,
      top: isNearTop ? `${this.chartTooltip.y + 18}px` : `${this.chartTooltip.y - 12}px`,
      transform: isNearTop ? "translate(-50%, 0)" : "translate(-50%, -100%)",
    };
  }

  showChartTooltip(
    event: MouseEvent | any,
    title: string,
    value: number | string,
    color?: string,
    percentOrTotal?: string | number
  ): void {
    let percentStr = "";
    if (typeof percentOrTotal === "string") {
      percentStr = percentOrTotal;
    } else if (typeof percentOrTotal === "number" && percentOrTotal > 0 && typeof value === "number") {
      const p = (value / percentOrTotal) * 100;
      percentStr = `${(isNaN(p) ? 0 : p).toFixed(1)}%`;
    }

    if (percentStr.includes("NaN") || percentStr.includes("undefined")) {
      percentStr = "";
    }

    const clientX = event.touches ? event.touches[0]?.clientX : event.clientX;
    const clientY = event.touches ? event.touches[0]?.clientY : event.clientY;

    const valNum = Number(value);
    this.chartTooltip = {
      visible: true,
      x: clientX || 0,
      y: clientY || 0,
      title: title || "",
      value: isNaN(valNum) ? 0 : valNum,
      percent: percentStr,
      color: color || "#3b82f6",
    };
  }

  moveChartTooltip(event: MouseEvent | any): void {
    if (this.chartTooltip.visible) {
      const clientX = event.touches ? event.touches[0]?.clientX : event.clientX;
      const clientY = event.touches ? event.touches[0]?.clientY : event.clientY;
      this.chartTooltip.x = clientX || this.chartTooltip.x;
      this.chartTooltip.y = clientY || this.chartTooltip.y;
    }
  }

  hideChartTooltip(): void {
    this.chartTooltip.visible = false;
  }

  @HostListener("window:scroll")
  onWindowScroll(): void {
    this.hideChartTooltip();
  }

  @HostListener("document:mouseleave")
  onDocumentMouseLeave(): void {
    this.hideChartTooltip();
  }

  get ancSvgSlices() {
    return this.getSvgDonutSlices(this.ancDonutData);
  }

  get hbncSvgSlices() {
    return this.getSvgDonutSlices(this.hbncDonutData);
  }

  get deathSvgSlices() {
    return this.getSvgDonutSlices(this.deathDonutData);
  }

  get pncSvgSlices() {
    return this.getSvgDonutSlices(this.pncDonutData);
  }

  // -------------------------------------------------------------
  // LIFECYCLE
  // -------------------------------------------------------------
  async ngOnInit() {
    this.initUserProfile();
    await this.initCurrentDate();

    try {
      const stored = this.appStateManage.StorageKey.getItem("FinancialYearResponses");
      const financialYearResponses = stored ? JSON.parse(stored) : [];

      if (financialYearResponses.length) {
        this.FinancialYearRef = financialYearResponses[0].financial_year_id;
        this.CurrentFinancialYear = financialYearResponses[0].short_name;
      }

      this.FilterList = DomainEnums.ReportFilterTypesListLang(false).filter(
        (r) => r.Ref !== ReportFilterTypes.Custom
      );

      await this.setFinancialYear(this.CurrentFinancialYear);
      await this.formulateFinancialYearList();

      const existingStoredState = this.filterStorage.getFilterState();
      if (!existingStoredState?.ReportFilterType) {
        this.ReportFilterType = ReportFilterTypes.Yearly;
        this.filterStorage.setFilterState({
          ...existingStoredState,
          FinancialYearRef: this.FinancialYearRef,
          ReportFilterType: ReportFilterTypes.Yearly,
        });
      }

      if (existingStoredState?.state_ids && existingStoredState.state_ids.length === 0) {
        await this.dashboardFilterService.LoadFilterDataUPTOStateLevel();
      }

      await this.applyDashboardStateOnFilterDropdown();
      await this.fetchTodaysTasks();
    } catch (err) {
      console.warn("[UpdatedDashboard] Initialization error, using fallback data:", err);
    }

    this.updateChartDataSources();
    this.lastUpdatedTime = new Date();
    this.initVisibilityListener();
    this.startStayTimer();

    this.translate.onLangChange.subscribe(() => {
      this.FilterList = [...this.FilterList];
      this.MonthList = [...this.MonthList];
      this.QuarterList = [...this.QuarterList];
      this.HalfYearList = [...this.HalfYearList];
      this.initUserProfile();
      this.todayDayOfWeek = this.getDayName(new Date());
      this.updateChartDataSources();
    });
  }

  ngOnDestroy(): void {
    this.clearStayTimer();
    if (this.updatedNoticeTimer) {
      clearTimeout(this.updatedNoticeTimer);
      this.updatedNoticeTimer = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  // -------------------------------------------------------------
  // REFRESH & STAY DETECTION METHODS
  // -------------------------------------------------------------
  startStayTimer(): void {
    this.clearStayTimer();
    this.staleStayTimer = setTimeout(() => {
      this.isDataStale = true;
      this.isBannerDismissed = false;
      this.isGuideDismissed = false;
      this.cdr.markForCheck();
    }, this.STALE_TIMEOUT_MS);
  }

  clearStayTimer(): void {
    if (this.staleStayTimer) {
      clearTimeout(this.staleStayTimer);
      this.staleStayTimer = null;
    }
  }

  resetStayTimer(): void {
    this.startStayTimer();
  }

  initVisibilityListener(): void {
    this.visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        if (this.lastUpdatedTime) {
          const elapsed = Date.now() - this.lastUpdatedTime.getTime();
          if (elapsed >= this.STALE_TIMEOUT_MS) {
            this.isDataStale = true;
            this.isBannerDismissed = false;
            this.isGuideDismissed = false;
            this.cdr.markForCheck();
          }
        }
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  dismissGuide(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isGuideDismissed = true;
    this.cdr.markForCheck();
  }

  getFormattedLastUpdated(): string {
    if (!this.lastUpdatedTime) return "";
    return this.lastUpdatedTime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  refreshDashboard = async (): Promise<void> => {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    this.cdr.markForCheck();
    try {
      await this.fetchDashboardData();
      await this.fetchTodaysTasks();
      this.isDataStale = false;
      this.isBannerDismissed = false;
      this.isGuideDismissed = false;
      this.justUpdated = true;

      if (this.updatedNoticeTimer) clearTimeout(this.updatedNoticeTimer);
      this.updatedNoticeTimer = setTimeout(() => {
        this.justUpdated = false;
        this.cdr.markForCheck();
      }, 3000);
    } catch (err) {
      console.error("[UpdatedDashboard] Error refreshing dashboard data:", err);
    } finally {
      this.isRefreshing = false;
      this.cdr.markForCheck();
    }
  };

  // -------------------------------------------------------------
  // USER PROFILE
  // -------------------------------------------------------------
  private initUserProfile(): void {
    const isMr = !!(this.langService && this.langService.currentLang === "mr");
    const name = isMr
      ? this.appStateManage.getUserDisplayNameMR()
      : this.appStateManage.getUserDisplayNameEN();

    if (name && name.trim()) {
      this.userDisplayName = name;
    } else {
      this.userDisplayName = isMr ? "श्रीमती विद्या विश्वास पाटील" : "Mrs. Vidya Vishwas Patil";
    }

    const role = isMr
      ? this.appStateManage.getRoleNameMR()
      : this.appStateManage.getRoleNameEN();

    if (role && role.trim()) {
      this.roleName = role;
    } else {
      this.roleName = "ASHA";
    }

    const phc = (isMr ? this.appStateManage.getPHCNameMR() : this.appStateManage.getPHCNameEN()) || "Sagroli";
    const sc = (isMr ? this.appStateManage.getSCNameMR() : this.appStateManage.getSCNameEN()) || "Sagroli";
    const village = (isMr ? this.appStateManage.getVillageNameMr() : this.appStateManage.getVillageNameEn()) || "Sagroli";

    if (isMr) {
      this.locationText = `प्रा.आ.केंद्र: ${phc} | उपकेंद्र: ${sc} | गाव: ${village}`;
    } else {
      this.locationText = `PHC: ${phc} | SC: ${sc} | Village: ${village}`;
    }
  }

  private async initCurrentDate(): Promise<void> {
    try {
      const date = await CurrentDateTimeRequest.GetCurrentDateTime();
      const today = (date ? this.parseServerDateTime(date) : null) || new Date();
      this.todayDateFormatted = this.getFormattedDate(today);
      this.todayDayOfWeek = this.getDayName(today);
    } catch (e) {
      console.warn("[UpdatedDashboard] Using local date fallback:", e);
      const today = new Date();
      this.todayDateFormatted = this.getFormattedDate(today);
      this.todayDayOfWeek = this.getDayName(today);
    }
  }

  /**
   * Parses server datetime string (yyyy-MM-dd-HH-mm-ss-SSS) into a JavaScript Date.
   *
   * @param strCDT Formatted datetime string
   */
  parseServerDateTime(strCDT: string): Date | null {
    try {
      if (!strCDT || typeof strCDT !== "string") return null;
      const parts = strCDT.split("-").map(Number);
      if (parts.length < 3 || isNaN(parts[0])) return null;
      const [year, month, day, hour = 0, minute = 0, second = 0, ms = 0] = parts;
      return new Date(year, month - 1, day, hour, minute, second, ms);
    } catch {
      return null;
    }
  }

  // -------------------------------------------------------------
  // FILTERS
  // -------------------------------------------------------------
  setFinancialYear = async (finYear: string) => {
    const range = this.customDate.getFinancialYearRange(finYear);
    if (range) {
      this.financialYearStart = range.start;
      this.financialYearEnd = range.end;
    }
  };

  formulateFinancialYearList = async () => {
    try {
      const lst = await FinancialYear.FetchEntireList(
        async (errMsg) => await this.uiUtils.showErrorMessage("Error", errMsg)
      );
      if (lst && lst.length > 0) {
        this.YearsList = lst;
      }
    } catch {
      // Fallback years if API not ready
      this.YearsList = [];
    }
  };

  applyDashboardStateOnFilterDropdown = async () => {
    const savedState = this.filterStorage.getFilterState();
    const reportFilterType = savedState?.ReportFilterType ?? ReportFilterTypes.Yearly;

    this.ReportFilterType = reportFilterType;
    this.applyFilterVisibility(reportFilterType);

    if (savedState?.Month) this.Month = savedState.Month;
    if (savedState?.QuarterlyYearType) this.QuarterlyYearType = savedState.QuarterlyYearType;
    if (savedState?.HalfYearType) this.HalfYearType = savedState.HalfYearType;

    await this.fetchDashboardData();
  };

  applyFilterVisibility(filterType: number) {
    this.IsShowMonth = filterType === ReportFilterTypes.Monthly;
    this.IsShowQuarter = filterType === ReportFilterTypes.Quarterly;
    this.IsShowHalfYear = filterType === ReportFilterTypes.HalfYearly;
    this.IsShowDates = filterType === ReportFilterTypes.Custom;
  }

  async onYearChanged(e: any) {
    const newValue = e.value;
    if (!newValue) return;

    this.FinancialYearRef = newValue;
    this.filterStorage.setFilterState({ FinancialYearRef: this.FinancialYearRef });

    const selectedYear = this.YearsList.find((y) => y.p.id === newValue);
    if (!selectedYear) return;

    this.CurrentFinancialYear = selectedYear.p.short_name;
    if ((this.FinancialYearRef && e.previousValue == 0) || e.previousValue == null) return;

    await this.fetchDashboardData();
  }

  getTranslatedName = (item: any): string => {
    if (!item) return "";
    return item.Name ? this.translate.instant(item.Name) : (item.name || item.display || item.toString());
  };

  OnFilterChange = async (filterRef: number) => {
    if (filterRef == 0 || filterRef == null) return;

    this.ReportFilterType = filterRef;
    this.Month = 0;
    this.QuarterlyYearType = 0;
    this.HalfYearType = 0;

    this.applyFilterVisibility(filterRef);
    this.filterStorage.setFilterState({
      ReportFilterType: filterRef,
      Month: 0,
      QuarterlyYearType: 0,
      HalfYearType: 0,
    });

    if (filterRef === ReportFilterTypes.Yearly) {
      await this.fetchDashboardData();
    }
  };

  onMonthChanged = async (e: any) => {
    if (!e.value) return;
    this.Month = e.value;
    this.filterStorage.setFilterState({ Month: this.Month });
    await this.fetchDashboardData();
  };

  onQuarterChanged = async (e: any) => {
    if (!e.value) return;
    this.QuarterlyYearType = e.value;
    this.filterStorage.setFilterState({ QuarterlyYearType: this.QuarterlyYearType });
    await this.fetchDashboardData();
  };

  onHalfYearChanged = async (e: any) => {
    if (!e.value) return;
    this.HalfYearType = e.value;
    this.filterStorage.setFilterState({ HalfYearType: this.HalfYearType });
    await this.fetchDashboardData();
  };

  onAreaChange(area: "rural" | "urban") {
    if (this.selectedArea === area) return;
    this.selectedArea = area;

    if (area === "urban") {
      this.isUrbanNoData = true;
      return;
    }
    this.isUrbanNoData = false;
    this.fetchDashboardData();
  }

  async openFilterPanel(): Promise<void> {
    this.dashboardFilterService.openFilter();
    await this.dashboardFilterService.LoadFilterDataUPTOStateLevel();
  }

  onFiltersApplied = async () => {
    this.filterStorage.setFilterState({
      FinancialYearRef: this.FinancialYearRef,
      ReportFilterType: this.ReportFilterType,
      Month: this.Month,
      QuarterlyYearType: this.QuarterlyYearType,
      HalfYearType: this.HalfYearType,
      state_ids: this.dashboardFilterService.state_ids,
      zone_ids: this.dashboardFilterService.zone_ids,
      district_ids: this.dashboardFilterService.district_ids,
      block_ids: this.dashboardFilterService.block_ids,
      bcm_post_ids: this.dashboardFilterService.bcm_post_ids,
      phc_ids: this.dashboardFilterService.phc_ids,
      af_ids: this.dashboardFilterService.af_ids,
      sc_ids: this.dashboardFilterService.sc_ids,
      village_ids: this.dashboardFilterService.village_ids,
      asha_ids: this.dashboardFilterService.asha_ids,
      asha_employee_record_ids: this.dashboardFilterService.asha_ids,
    });
    this.isDataStale = false;
    this.isGuideDismissed = false;
    await this.fetchDashboardData();
    this.fetchTodaysTasks();
    this.resetStayTimer();
  };

  onFiltersCleared = () => { };

  // -------------------------------------------------------------
  // FETCH DASHBOARD DATA (LIVE WITH STATIC FALLBACK)
  // -------------------------------------------------------------

  fetchDashboardData = async () => {
    try {
      const req = new DashboardsFetchRequest();

      if (this.FinancialYearRef) req.financial_year_ids.push(this.FinancialYearRef);
      if (this.ReportFilterType) req.report_filter_type = this.ReportFilterType;
      if (this.Month) req.month = this.Month;
      if (this.QuarterlyYearType) req.quarterly_year_type = this.QuarterlyYearType;
      if (this.HalfYearType) req.half_year_type = this.HalfYearType;

      if (this.dashboardFilterService.state_ids.length > 0) req.state_ids = this.dashboardFilterService.state_ids;
      if (this.dashboardFilterService.zone_ids.length > 0) req.zone_ids = this.dashboardFilterService.zone_ids;
      if (this.dashboardFilterService.district_ids.length > 0) req.district_ids = this.dashboardFilterService.district_ids;
      if (this.dashboardFilterService.block_ids.length > 0) req.block_ids = this.dashboardFilterService.block_ids;
      if (this.dashboardFilterService.phc_ids.length > 0) req.phc_ids = this.dashboardFilterService.phc_ids;
      if (this.dashboardFilterService.sc_ids.length > 0) req.sc_ids = this.dashboardFilterService.sc_ids;
      if (this.dashboardFilterService.bcm_post_ids.length > 0) req.bcm_post_ids = this.dashboardFilterService.bcm_post_ids;
      if (this.dashboardFilterService.village_ids.length > 0) req.village_ids = this.dashboardFilterService.village_ids;
      if (this.dashboardFilterService.asha_ids.length > 0) req.asha_employee_record_ids = this.dashboardFilterService.asha_ids;

      const pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(req.FormulateTransportData());
      const tr = await ServerCommunicatorService.GetInstance().sendHttpRequestWithoutLoader(pktRequest);

      if (!tr.Successful) {
        return;
      }

      const tdResponse = JSON.parse(tr.Tag) as TransportData;

      // Extract server as_on_date_formatted from the fetch request collection
      let serverDate = "";
      const dcs = DataContainerService.GetInstance();
      if (dcs.CollectionExists(tdResponse.MainData, DashboardsFetchRequest.FetchRequestType)) {
        const fetchColl = dcs.GetCollection(tdResponse.MainData, DashboardsFetchRequest.FetchRequestType);
        if (fetchColl?.Entries?.length) {
          const reqItem = fetchColl.Entries[0] as DashboardsFetchRequest;
          if (reqItem?.as_on_date_formatted) {
            serverDate = reqItem.as_on_date_formatted;
          }
        }
      }

      const res: Dashboards[] = Dashboards.FromTransportData(tdResponse);
      if (!res || res.length === 0) {
        if (serverDate) {
          this.serverAsOnDateFormatted = serverDate;
          this.cdr.markForCheck();
        }
        return;
      }

      const data: Dashboards = res[0];
      const kf = data.key_figures;
      const ov = data.overview;
      console.log("data", data);

      if (!serverDate && data.as_on_date_formatted) {
        serverDate = data.as_on_date_formatted;
      }
      if (serverDate) {
        this.serverAsOnDateFormatted = serverDate;
      }

      // ---------------- Key figures ----------------
      this.totalPopulationCoveredByAsha = Number(kf?.total_population_covered_by_asha) || 0;
      this.totalPopulationPercentage = Number(kf?.total_population_covered_by_asha_percentage) || 0;
      this.totalPopulationAssignedToAsha = Number(kf?.total_population_assigned_to_asha) || 0;
      this.noOfFamilies = Number(kf?.no_of_families) || 0;
      this.noOfFamiliesPercentage = Number(kf?.no_of_families_percentage) || 0;
      this.childrenBelowSixYears = Number(kf?.no_of_children_from_zero_to_six_years) || 0;
      this.childrenBelowFiveYearsPercentage = Number(kf?.no_of_children_from_zero_to_six_years_percentage) || 0;
      this.membersAboveThirtyYears = Number(kf?.no_of_members_above_thirty_years) || 0;
      this.membersAboveThirtyYearsPercentage = Number(kf?.no_of_members_above_thirty_years_percentage) || 0;
      this.membersAboveSixtyFiveYears = Number(kf?.no_of_members_above_sixty_five_years) || 0;
      this.membersAboveSixtyFiveYearsPercentage = Number(kf?.no_of_members_above_sixty_five_years_percentage) || 0;

      // ---------------- Family registration ----------------
      this.familyApprovedTotal = Number(ov?.approved_families) || 0;
      this.familyPendingTotal = Number(ov?.pending_families) || 0;
      this.familyTotalFamilies = Number(this.familyApprovedTotal + this.familyPendingTotal);

      const native = Number(ov?.native_families) || 0;
      const migrated = Number(ov?.migrated_families) || 0;
      const totalNM = native + migrated;
      if (totalNM > 0) {
        const nativeRatio = native / totalNM;
        this.familyApprovedNative = Math.round(this.familyApprovedTotal * nativeRatio);
        this.familyApprovedMigrated = Math.max(0, this.familyApprovedTotal - this.familyApprovedNative);
        this.familyPendingNative = Math.round(this.familyPendingTotal * nativeRatio);
        this.familyPendingMigrated = Math.max(0, this.familyPendingTotal - this.familyPendingNative);
      } else {
        this.familyApprovedNative = 0;
        this.familyApprovedMigrated = 0;
        this.familyPendingNative = 0;
        this.familyPendingMigrated = 0;
      }

      // ---------------- Eligible couple ----------------
      this.ecrTotal = Number(ov?.total_eligible_couple) || 0;
      this.ecrMaleSterilization = Number(ov?.male_sterilization) || 0;
      this.ecrFemaleSterilization = Number(ov?.female_sterilization) || 0;
      this.ecrSterilizedTotal = Number(ov?.sterilized_couple) || (this.ecrMaleSterilization + this.ecrFemaleSterilization);
      this.ecrInfertileCouples = Number(ov?.infertile_couple) || 0;
      this.ecrFamilyPlanningUsage = Number(ov?.family_planning_used) || 0;

      const cpr = Number(ov?.couple_protection_rate_percentage);
      if (!isNaN(cpr) && cpr > 0) {
        this.ecrProtectionRate = cpr;
      } else if (this.ecrTotal > 0 && this.ecrFamilyPlanningUsage > 0) {
        this.ecrProtectionRate = Math.round((this.ecrFamilyPlanningUsage / this.ecrTotal) * 100);
      } else {
        this.ecrProtectionRate = 0;
      }

      // ---------------- ANC ----------------
      this.ancTotal = Number(ov?.total_anc) || 0;
      this.ancFullyProtected = Number(ov?.fully_protected) || 0;
      this.ancExpectedDeliveries = Number(ov?.expected_deliveries) || 0;
      this.ancHighRisk = Number(ov?.high_risk_maata) || 0;
      this.ancNormalMaata = Number(ov?.normal_maata) || 0;
      this.ancPregnancyRegistered12Weeks = Number(ov?.pregnancy_registered) || 0;
      this.ancSevereAnemia = Number(ov?.severe_anemia) || 0;

      // ---------------- Birth ----------------
      this.birthTotal = Number(ov?.total_birth) || 0;
      this.birthMale = Number(ov?.total_male_birth) || 0;
      this.birthFemale = Number(ov?.total_female_birth) || 0;

      // ---------------- HBNC ----------------
      this.hbncTotal = Number(ov?.total_hbnc) || 0;
      this.hbncRegistered = Number(ov?.total_hbnc_registered) || 0;
      this.hbncVisitsCompleted = Number(ov?.total_hbnc_visits_completed) || 0;
      this.hbncZeroVisitsCompleted = Number(ov?.zero_visits_completed) || 0;
      this.hbncDefectiveChildren = Number(ov?.defective_children) || 0;
      this.hbncChildDeath = Number(ov?.child_death) || 0;
      this.hbncNICU = Number(ov?.nicu) || 0;
      this.hbncNBSU = Number(ov?.nbsu) || 0;
      this.hbncSNCU = Number(ov?.sncu) || 0;

      // ---------------- Child immunization ----------------
      this.immunizationFully = Number(ov?.fully_immunized_children) || 0;
      this.immunizationNoVaccine = Number(ov?.children_with_no_vaccination) || 0;
      this.immunizationStartedNotCompleted = Number(ov?.children_with_incomplete_immunization) || 0;
      this.immunizationTotal = Number(ov?.total_child_immunized)
        || (this.immunizationFully + this.immunizationNoVaccine + this.immunizationStartedNotCompleted);

      // ---------------- Death ----------------
      this.deathTotal = Number(ov?.total_death) || 0;
      this.deathANC = Number(ov?.anc_death) || 0;
      this.deathStillBirth = Number(ov?.still_birth) || 0;
      this.death0to5Years = Number(ov?.zero_to_five_years_death) || 0;
      this.death30PlusYears = Number(ov?.thirty_above_death) || 0;
      this.death65PlusYears = Number(ov?.sixtyfive_above_death) || 0;

      // ---------------- PNC ----------------
      this.pncTotal = Number(ov?.total_pnc) || 0;
      this.pncVisitsCompleted = Number(ov?.total_pnc_visits_completed) || 0;
      this.pncHighRisk = Number(ov?.high_risk_pnc) || 0;
      this.pncZeroVisitCompleted = Number(ov?.zero_visit_completed_pnc) || 0;
      this.pncNormal = Number(ov?.normal_pnc) || 0;

      // ---------------- Pregnancy loss ----------------
      this.pregnancyLossAbortion = Number(ov?.abortion) || 0;
      this.pregnancyLossMiscarriage = Number(ov?.miscarriage) || 0;
      this.pregnancyLossTotal = Number(ov?.total_pregnancy_loss)
        || (this.pregnancyLossAbortion + this.pregnancyLossMiscarriage);
    } catch (err) {
      console.log("[UpdatedDashboard] Live fetch skipped or failed:", err);
    } finally {
      this.lastUpdatedTime = new Date();
      this.resetStayTimer();
      this.updateChartDataSources();
      this.cdr.markForCheck();
    }
  };

  private useStaticTodayTasks(): void {
    this.allTodayTasks = [];          // empty – show "No tasks for today"
    this.isTodayTasksLive = false;
    this.cdr.markForCheck();
  }

  fetchTodaysTasks = async () => {
    try {
      const req = new TodaysTaskFetchRequest();
      const f = this.dashboardFilterService;
      if (f.state_ids.length) req.state_ids = f.state_ids;
      if (f.zone_ids.length) req.zone_ids = f.zone_ids;
      if (f.district_ids.length) req.district_ids = f.district_ids;
      if (f.block_ids.length) req.block_ids = f.block_ids;
      if (f.phc_ids.length) req.phc_ids = f.phc_ids;
      if (f.sc_ids.length) req.sc_ids = f.sc_ids;
      if (f.village_ids.length) req.village_ids = f.village_ids;
      if (f.asha_ids.length) req.asha_employee_record_ids = f.asha_ids;

      const pkt = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(req.FormulateTransportData());
      const tr = await ServerCommunicatorService.GetInstance().sendHttpRequestWithoutLoader(pkt);

      if (!tr?.Successful) {
        this.useStaticTodayTasks(); // or clearTodayTasks() if no mock
        return;
      }

      const res = TodaysTask.FromTransportData(JSON.parse(tr.Tag) as TransportData);
      console.log("[TodayTasks] res:", res);

      if (!res?.length) {
        this.useStaticTodayTasks();
        return;
      }

      const pad = (v: number) => String(v).padStart(2, "0");

      this.allTodayTasks = [...res]
        .sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0))
        .map((item) => {
          const count = Number(item.total_count) || 0;
          const id = (item.name_en || "task").toLowerCase().replace(/\s+/g, "_");
          const entityType = Number(item.from_entity_type) || 0;

          return {
            id,
            title: item.name_en || "Task",
            titleMr: item.name_mr || item.name_en || "काम",
            count: String(count).padStart(2, "0"),
            iconImg: getTodayTaskIcon(entityType, item.name_en || id),
            emoji: "📋",
            from_entity_type: entityType,
          } as TodayTask;
        })
        .filter((x) => Number(x.count) > 0);  // only hide zero counts

      this.isTodayTasksLive = true;
      this.cdr.markForCheck();
    } catch (err) {
      console.warn("[UpdatedDashboard] Today's task fetch failed:", err);
      this.useStaticTodayTasks();
    }
  };

  // -------------------------------------------------------------
  // NAVIGATION & DRILL-DOWN HANDLERS
  // -------------------------------------------------------------
  private navigateToDrillDown = async (
    requestType: DrillDownRequestTypes,
    subType: DrillDownSubTypes = DrillDownSubTypes.none
  ) => {
    const requiresSubType = DRILLDOWN_TYPES_REQUIRING_SUBTYPE.includes(requestType);
    if (requiresSubType && subType === DrillDownSubTypes.none) {
      console.warn(`[UpdatedDashboard] "${DrillDownRequestTypes[requestType]}" requires a subType.`);
    }

    this.filterStorage.setDrillDownContext(requestType, subType);
    await this.router.navigate([DRILLDOWN_ROUTE_PATH]);
  };

  // Task navigation
  async onTaskClick(taskId: string) {
    const task = this.allTodayTasks.find((t) => t.id === taskId);
    if (!task) return;

    // Family Registration opens its register directly from the dashboard.
    if (task.from_entity_type === FromEntityTypes.family_registration) {
      await this.router.navigate([SUBMENU_ID.FAMILY_REGISTRATION]);
      return;
    }

    const nav = {
      taskId: task.id,
      title: task.title || "",
      titleMr: task.titleMr || "",
      from_entity_type: task.from_entity_type || 0,
    };

    // backup so a browser refresh (F5) on the details page still works
    sessionStorage.setItem("todaysTaskDetailsNav", JSON.stringify(nav));

    await this.router.navigate([SUBMENU_ID.TODAYS_TASK_LIST_DETAILS], { state: nav });
  }

  async onViewAllTasks() {
    await this.router.navigate([SUBMENU_ID.TODAYS_TASK_LIST]);
  }

  // Key Figures
  onKeyFigureClick(type: "population" | "families" | "children" | "above30" | "above65") {
    switch (type) {
      case "population":
        this.navigateToDrillDown(DrillDownRequestTypes.total_population_covered_by_asha);
        break;
      case "families":
        this.navigateToDrillDown(DrillDownRequestTypes.no_of_familes);
        break;
      case "children":
        this.navigateToDrillDown(DrillDownRequestTypes.children_0_to_6_yeras);
        break;
      case "above30":
        this.navigateToDrillDown(DrillDownRequestTypes.member_above_30_years);
        break;
      case "above65":
        this.navigateToDrillDown(DrillDownRequestTypes.member_above_65_years);
        break;
    }
  }

  // Family registration clicks
  // FIX: Added "Migrated" (and "Native" for completeness) to the union type and map
  onFamilyClick(
    type:
      | "Total"
      | "Approved"
      | "Pending"
      | "ApprovedNative"
      | "ApprovedMigrated"
      | "PendingNative"
      | "PendingMigrated"
      | "Migrated"
      | "Native" = "Total"
  ) {
    if (type === "Total") return;
    const map: Record<string, DrillDownSubTypes> = {
      Total: DrillDownSubTypes.family_registration_total,
      Approved: DrillDownSubTypes.family_registration_approved,
      Pending: DrillDownSubTypes.family_registration_pending,
      ApprovedNative: DrillDownSubTypes.family_registration_approved_native,
      ApprovedMigrated: DrillDownSubTypes.family_registration_approved_migrated,
      PendingNative: DrillDownSubTypes.family_registration_pending_native,
      PendingMigrated: DrillDownSubTypes.family_registration_pending_migrated,
      // Total Migrated (Approved Migrated + Pending Migrated)
      Migrated: DrillDownSubTypes.family_registration_approved_migrated,
      // Total Native (Approved Native + Pending Native) – in case you add a Native chip later
      Native: DrillDownSubTypes.family_registration_approved_native,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.family_registration, map[type]);
  }

  // Eligible couple clicks
  onEcrClick(type: "eligible" | "sterilized" | "maleSterilization" | "femaleSterilization" | "infertile" | "usage") {
    const map: Record<string, DrillDownSubTypes> = {
      eligible: DrillDownSubTypes.eligible_couple_register_eligible_couples_total,
      infertile: DrillDownSubTypes.eligible_couple_register_infertile,
      sterilized: DrillDownSubTypes.eligible_couple_register_sterilized_couples,
      maleSterilization: DrillDownSubTypes.eligible_couple_register_sterilized_couples_male,
      femaleSterilization: DrillDownSubTypes.eligible_couple_register_sterilized_couples_female,
      usage: DrillDownSubTypes.eligible_couple_register_family_planning_usage,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.eligible_couple_register, map[type]);
  }

  // ANC clicks
  onAncClick(type: "total" | "fullyProtected" | "normal" | "expected" | "within12Weeks" | "highRisk" | "severeAnemia") {
    if (type === "total") return;
    const map: Record<string, DrillDownSubTypes> = {
      total: DrillDownSubTypes.anc_total,
      fullyProtected: DrillDownSubTypes.anc_fully_protected_anc,
      normal: DrillDownSubTypes.anc_normal_maata,
      expected: DrillDownSubTypes.anc_expected_deliveries,
      within12Weeks: DrillDownSubTypes.anc_pregnancy_registered_within_12_weeks,
      highRisk: DrillDownSubTypes.anc_high_risk_maata,
      severeAnemia: DrillDownSubTypes.anc_severe_anemia,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.anc, map[type]);
  }

  // Birth clicks
  onBirthClick(type: "total" | "male" | "female") {
    if (type === "total") return;
    const map: Record<string, DrillDownSubTypes> = {
      total: DrillDownSubTypes.birth_total,
      male: DrillDownSubTypes.birth_boy,
      female: DrillDownSubTypes.birth_girl,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.birth_register, map[type]);
  }

  // HBNC clicks
  onHbncClick(type: "total" | "registered" | "completed" | "zeroVisits" | "defective" | "death" | "nicu" | "kmc" | "sncu") {
    if (type === "total") return;
    const map: Record<string, DrillDownSubTypes> = {
      total: DrillDownSubTypes.hbnc_total_birth,
      registered: DrillDownSubTypes.hbnc_total_register,
      completed: DrillDownSubTypes.hbnc_total_visits_completed,
      zeroVisits: DrillDownSubTypes.hbnc_zero_visits_completed,
      defective: DrillDownSubTypes.hbnc_defective_children,
      death: DrillDownSubTypes.hbnc_child_death,
      nicu: DrillDownSubTypes.hbnc_nicu,
      kmc: DrillDownSubTypes.hbnc_nbsu, // KMC card maps to the BNSU sub-type (no separate KMC value in the enum)
      sncu: DrillDownSubTypes.hbnc_sncu,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.hbnc, map[type]);
  }

  // Immunization clicks
  onImmunizationClick(type: "fully" | "noVaccine" | "startedNotCompleted") {
    const map: Record<string, DrillDownSubTypes> = {
      fully: DrillDownSubTypes.immunization_fully,
      noVaccine: DrillDownSubTypes.immunization_zero_dose,
      startedNotCompleted: DrillDownSubTypes.immunization_started_not_completed,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.child_immunization, map[type]);
  }

  // Death clicks
  onDeathClick(type: "total" | "anc" | "still" | "0to5" | "30plus" | "65plus") {
    if (type === "total") return;
    const map: Record<string, DrillDownSubTypes> = {
      total: DrillDownSubTypes.death_total,
      anc: DrillDownSubTypes.death_anc_death,
      still: DrillDownSubTypes.death_still_birth,
      "0to5": DrillDownSubTypes.death_0_to_5_years_death,
      "30plus": DrillDownSubTypes.death_above_30_years_death,
      "65plus": DrillDownSubTypes.death_above_65_years_death,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.death_register, map[type]);
  }

  // PNC clicks
  onPncClick(type: "total" | "completed" | "zeroVisits" | "highRisk" | "normal") {
    if (type === "total") return;
    const map: Record<string, DrillDownSubTypes> = {
      total: DrillDownSubTypes.pnc_total,
      completed: DrillDownSubTypes.pnc_total_pnc_visits_completed,
      zeroVisits: DrillDownSubTypes.pnc_zero_visit_completed,
      highRisk: DrillDownSubTypes.pnc_high_risk_pnc,
      normal: DrillDownSubTypes.pnc_normal_pnc,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.pnc, map[type]);
  }

  // Pregnancy loss clicks
  onPregnancyLossClick(type: "abortion" | "miscarriage") {
    const map: Record<string, DrillDownSubTypes> = {
      abortion: DrillDownSubTypes.pregnancy_loss_abortion,
      miscarriage: DrillDownSubTypes.pregnancy_loss_miscarriage,
    };
    this.navigateToDrillDown(DrillDownRequestTypes.pregnancy_loss, map[type]);
  }
}
